import Resolver from "@/api/resolver";
import store from "@/store";
import { navigationNamespace } from "@/store/modules/navigation";
import { patientNamespace } from "@/store/modules/patient";
import Vue from "vue";
import Router, { Location } from "vue-router";
import HelpPages from "./components/help/";

Vue.use(Router);

const parseArguments = (route: any) => {
  const room = parseInt(route.params.room, 10);
  const params = { ...route.params };
  params.room = room;
  return params;
};

const router = new Router({
  routes: [
    {
      path: "/error/:errorKey",
      name: "error",
      props: true,
      component: () => import(/* webpackChunkName: "error" */ "./views/Error"),
      meta: {
        guest: true,
        noRedirect: true
      }
    },
    {
      path: "/login/:area?",
      name: "login",
      component: () => import(/* webpackChunkName: "login" */ "./views/Login"),
      meta: {
        guest: true,
        noRedirect: true
      }
    },
    {
      path: "/productSelect/",
      name: "productSelect",
      component: () =>
        import(/* webpackChunkName: "product-select" */ "./views/ProductSelect")
    },
    {
      path: "/status/",
      name: "status",
      component: () =>
        import(/* webpackChunkName: "product-select" */ "./views/Status")
    },
    {
      path: "/:area/census/:view?/:unit?/:sort?/:filters?",
      name: "census",
      component: () => import(/* webpackChunkName: "census" */ "./views/Census")
    },
    {
      path: "/:area/patient/:room",
      name: "body",
      props: parseArguments,
      component: () => import(/* webpackChunkName: "body" */ "./views/Body")
    },
    {
      path: "/:area/help/",
      name: "help",
      component: () => import(/* webpackChunkName: "help" */ "./views/Help"),
      redirect: "/:area/help/Instructions",
      props: true,
      children: [
        { path: "Instructions", component: HelpPages.Instructions },
        { path: "ColorLimits", component: HelpPages.ColorLimits },
        { path: "Alerts", component: HelpPages.Alerts },
        { path: "Paging", component: HelpPages.Paging },
        { path: "StandardReferences", component: HelpPages.StandardReferences },
        {
          path: "EmergencyReferences",
          component: HelpPages.EmergencyReferences
        },
        { path: "Accessibility", component: HelpPages.Accessibility },
        { path: "Verification", component: HelpPages.Verification }
      ]
    },
    {
      path: "/:area/tower/:room/:page",
      name: "tower",
      props: parseArguments,
      component: () => import(/* webpackChunkName: "tower" */ "./views/Tower")
    },
    {
      path: "*",
      redirect: `/login`
    }
  ]
});

router.beforeEach(async (to, _from, next) => {
  if (to.name === "login" || Resolver.demo) {
    next();
  } else {
    if (store.getters["user/isLoggedIn"]) {
      next();
    } else {
      await store.dispatch("user/check");
      if (store.getters["user/isLoggedIn"]) {
        next();
      } else {
        // Avoid redirecting to a guest page like error or login
        if (!to.matched.some(to => to.meta.noRedirect)) {
          store.dispatch("navigation/redirectTo", to);
        }
        next("login");
      }
    }
  }
});

router.beforeResolve((to, from, next) => {
  if (to.query.wrapper) {
    router.app.$store.dispatch(
      `${navigationNamespace.namespace}/inWrapper`,
      to.query.wrapper
    );
  }

  if (to.query.theme) {
    router.app.$store.dispatch(
      `${navigationNamespace.namespace}/darkMode`,
      to.query.theme == "dark"
    );
    const { query, name, params } = to;
    delete query.theme;
    const nextArgument: Location = { query, params };
    if (name) {
      nextArgument.name = name;
    }
    next(nextArgument);
    return;
  }

  if (to.query.staticTitle) {
    router.app.$store.dispatch(
      `${navigationNamespace.namespace}/staticTitle`,
      to.query.staticTitle == "true"
    );
    const { query, name, params } = to;
    delete query.staticTitle;
    const nextArgument: Location = { query, params };
    if (name) {
      nextArgument.name = name;
    }
    next(nextArgument);
    return;
  }

  if (to.query.singlePatientView) {
    router.app.$store.dispatch(
      `${patientNamespace.namespace}/singlePatientView`
    );
    const { query, name, params } = to;
    delete query.singlePatientView;
    const nextArgument: Location = { query, params };
    if (name) {
      nextArgument.name = name;
    }
    next(nextArgument);
    return;
  }

  if (to.query.mode) {
    router.app.$store.dispatch(
      `${patientNamespace.namespace}/toggleMode`,
      to.query.mode
    );
    const { query, name, params } = to;
    delete query.mode;
    const nextArgument: Location = { query, params };
    if (name) {
      nextArgument.name = name;
    }
    next(nextArgument);
    return;
  }

  next();
});

export default router;
